import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useQueryClient } from '@tanstack/react-query';

import { DAItem, useDAList } from '../../hooks/useApiQueries';
import useSearchHandler from '../../hooks/useSearchHandler';
import { formatDaFeeTtc, formatDateToDDMMYYYY, getDaFeeTtc } from '../../utils';
import { useAuth } from '../AuthContext';
import PaymentModal from '../CGList/PaymentModal';
import CommentsManager from '../Comments/CommentsManager';
import SubmitFormDA from '../DealsList/DealStageActions/SubmitFormDA';

interface DATableProps {
  title: string;
  daList: DAItem[];
  onRefresh: () => Promise<void>;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: string;
  label: string;
  numeric: boolean;
}

const DATable = ({ title, daList, onRefresh }: DATableProps) => {
  const { t } = useTranslation('common');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('RegistrationID');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const navigate = useNavigate();

  useEffect(() => {
    setPage(0);
  }, [daList]);

  const headCells: readonly HeadCell[] = [
    { id: 'regPlate', label: 'Immat', numeric: false },
    { id: 'RegistrationID', label: 'ID', numeric: false },
    { id: 'SellerBuyer', label: 'Vendeur / Acheteur', numeric: false },
    { id: 'purchaseDateTime', label: 'Date de vente', numeric: false },
    { id: 'createdAt', label: 'Date de création', numeric: false },
    { id: 'Comments', label: 'Commentaires', numeric: false },
    { id: 'Actions', label: 'Actions', numeric: false },
  ];

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function getComparator(order: Order, orderBy: string): (a: DAItem, b: DAItem) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a: DAItem, b: DAItem, orderBy: string) {
    const aValue = (a as any)[orderBy];
    const bValue = (b as any)[orderBy];
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }

  const handleOpenPaymentModal = (invoice: string, daFeeHt: number | string) => {
    setSelectedInvoice(invoice);
    setPrice(getDaFeeTtc(daFeeHt));
    setOpenPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  function handleDAClick(registrationId: number) {
    navigate(`/form-da?registrationId=${registrationId}`);
  }

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      DA_REQUEST: 'En attente de paiement',
      DA_PROCESSING: 'En attente de traitement',
      DA_COMPLETED: 'Terminé',
    };
    return statusMap[status] || status;
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'DA_REQUEST':
        return {
          backgroundColor: '#FF6060',
          color: 'white',
          opacity: 1,
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          display: 'inline-block',
        };
      case 'DA_PROCESSING':
        return {
          backgroundColor: '#FF6060',
          color: 'white',
          opacity: 0.7,
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          display: 'inline-block',
        };
      case 'DA_COMPLETED':
        return {
          color: '#008000',
          fontWeight: 'bold',
          fontSize: '16px',
        };
      default:
        return {
          color: 'inherit',
          fontWeight: 'normal',
        };
    }
  };

  const sortedRows = useMemo(() => {
    return [...daList].sort(getComparator(order, orderBy));
  }, [daList, order, orderBy]);

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', fontSize: '18px' }}>
        {title}
      </Typography>
      <Paper sx={{ width: '100%', mb: 2 }}>
        {sortedRows.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
            Pas de déclaration d'achat dans cette catégorie
          </Typography>
        ) : (
          <>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'black' }}>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? 'right' : 'left'}
                      sortDirection={orderBy === headCell.id ? order : false}
                      sx={{
                        fontWeight: 'bold',
                        background: 'black',
                        ...(headCell.id === 'Actions' && { width: 155 }),
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                        sx={{ background: 'black' }}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((da: DAItem) => (
                  <TableRow hover key={da.RegistrationID}>
                    <TableCell sx={{ borderBottom: '2px solid' }}>
                      <Link href={`/form-da?registrationId=${da.RegistrationID}`}>{da.regPlate}</Link>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: '2px solid' }}>
                      {da.RegistrationID}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '2px solid' }}>
                      <Typography fontSize="14px" variant="body2">
                        {da.SellerClientType === 'pro' && da.SellerCompanyName
                          ? da.SellerCompanyName
                          : `${da.SellerFirstName || ''} ${da.SellerLastName || ''}`.trim() || 'Non renseigné'}
                      </Typography>
                      <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
                      <Typography fontSize="14px" variant="body2">
                        {da.BuyerClientType === 'pro' && da.BuyerCompanyName
                          ? da.BuyerCompanyName
                          : `${da.BuyerFirstName || ''} ${da.BuyerLastName || ''}`.trim() || 'Non renseigné'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '2px solid' }}>
                      {formatDateToDDMMYYYY(da.purchaseDateTime)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '2px solid' }}>{formatDateToDDMMYYYY(da.createdAt)}</TableCell>
                    <TableCell sx={{ minWidth: '200px', borderBottom: '2px solid' }}>
                      <CommentsManager
                        registrationId={da.RegistrationID}
                        internalComments={da.comments || []}
                        userRole={userRole}
                        type="da"
                        refetch={async () => await onRefresh()}
                        maxWidth={200}
                        regPlate={da.regPlate}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: '2px solid' }}>
                      <Stack direction="column" spacing={1} alignItems="center">
                        {da.status === 'DA_REQUEST' && (
                          <Button
                            sx={{
                              fontSize: 14,
                              background: '#FF6060',
                              opacity: 1,
                              fontWeight: 600,
                              width: 152,
                            }}
                            className="dealActionButton"
                            onClick={() => handleOpenPaymentModal(da.invoice_number || '', da.daFee)}
                            variant="contained"
                          >
                            Payer
                          </Button>
                        )}
                        {da.status === 'DA_REQUEST' && (
                          <>
                            <Typography variant="body2" sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}>
                              Référence pour le virement : {da.invoice_number}
                            </Typography>
                            <Typography variant="body2" sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}>
                              Prix à payer : {formatDaFeeTtc(da.daFee)}
                            </Typography>
                          </>
                        )}
                        <Button
                          sx={{
                            fontSize: 14,
                            width: 152,
                            display: da.invoice_url_firebase ? 'block' : 'none',
                          }}
                          className="secondaryDealActionButton"
                          variant="contained"
                          onClick={() => window.open(da.invoice_url_firebase, '_blank')}
                        >
                          Voir la facture
                        </Button>

                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                          {da.invoice_number} {da.invoice_status && `/ ${t(`invoiceStatus.${da.invoice_status}`)}`}
                        </Typography>

                        <Button
                          sx={{ fontSize: 14, width: 152 }}
                          className="secondaryDealActionButton"
                          variant="contained"
                          onClick={() => handleDAClick(da.RegistrationID)}
                        >
                          Voir la demande
                        </Button>

                        <SubmitFormDA registrationId={da.RegistrationID} userRole={userRole} onRefresh={onRefresh} />

                        <Typography
                          align="center"
                          sx={{
                            fontSize: '16px',
                            lineHeight: '1.25em',
                            ...getStatusStyle(da.status),
                          }}
                          variant="body2"
                        >
                          {getStatusLabel(da.status)}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 20, 50]}
              component="div"
              count={sortedRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>
      <PaymentModal
        open={openPaymentModal}
        onClose={handleClosePaymentModal}
        price={price}
        invoiceNumber={selectedInvoice}
      />
    </Box>
  );
};

const AgencyDAList = ({ orgId }: { orgId: number }) => {
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const userPipedrive = currentUser?.data?.data?.idPipedrive ?? 188;
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const { searchQuery, hasSearched, handleSearch, resetSearch } = useSearchHandler();

  // Fetch all DA data for the agency
  const {
    data,
    isLoading: isLoadingDA,
    refetch,
  } = useDAList(
    userPipedrive,
    userRole,
    1,
    1000, // Fetch all at once for grouping
    searchQuery,
    undefined, // No status filter to get all
    'R.id',
    'DESC',
    false,
    orgId,
  );

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    try {
      queryClient.invalidateQueries({ queryKey: ['useDAList'] });
      await refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, refetch]);

  const daList: DAItem[] = data?.data || [];

  // Group DA by status
  const daPending = daList.filter((da: DAItem) => da.status === 'DA_REQUEST'); // En attente de paiement
  const daProcessing = daList.filter((da: DAItem) => da.status === 'DA_PROCESSING'); // En attente de traitement
  const daCompleted = daList.filter((da: DAItem) => da.status === 'DA_COMPLETED'); // Terminé

  const filterDA = (daList: DAItem[]) => {
    if (!searchQuery) return daList;

    const query = searchQuery.toLowerCase();
    return daList.filter((da) => {
      return (
        da.regPlate?.toLowerCase().includes(query) ||
        da.RegistrationID?.toString().includes(query) ||
        da.SellerFirstName?.toLowerCase().includes(query) ||
        da.SellerLastName?.toLowerCase().includes(query) ||
        da.BuyerFirstName?.toLowerCase().includes(query) ||
        da.BuyerLastName?.toLowerCase().includes(query)
      );
    });
  };

  const allDAFiltered = hasSearched ? filterDA(daList) : [];

  const handleSearchChange = (value: string) => {
    if (!value.trim()) {
      resetSearch(refetch);
    } else {
      handleSearch(value, refetch);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ marginTop: 0 }}>
        Vos déclarations d'achat
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Liste des déclarations d'achat de votre agence.
      </Typography>
      <Grid container item xs={12} sm={12} marginBottom={4} marginTop={0}>
        <Grid item xs={6} sm={5}>
          <TextField
            label="Rechercher"
            className="inputElement"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            size="small"
            sx={{
              '& .MuiInputLabel-root': {
                top: '5px',
              },
              '& .MuiInputLabel-shrink': {
                top: 0,
              },
            }}
          />
          <Button
            type="button"
            variant="contained"
            className="ActionButton"
            onClick={() => handleSearch(searchQuery, refetch)}
          >
            Rechercher
          </Button>
          {hasSearched && (
            <Button
              className="secondaryDealActionButton"
              sx={{ left: '10px', top: '-15px', height: '55px' }}
              onClick={() => resetSearch(refetch)}
            >
              Revenir à la liste
            </Button>
          )}
        </Grid>
        <Grid item xs={6} sm={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              marginRight: 0,
              marginLeft: 0,
            }}
          >
            <Button
              type="button"
              variant="contained"
              className="ActionButton"
              startIcon={<SyncIcon />}
              onClick={handleRefresh}
            >
              Rafraichir la liste
            </Button>
          </Box>
        </Grid>
      </Grid>
      {!orgId || orgId <= 0 ? (
        <Typography variant="h6" sx={{ mt: 4, textAlign: 'center' }}>
          Il semblerait y avoir un problème dans la configuration de votre compte, veuillez nous contacter au 07 80 94
          97 37.
        </Typography>
      ) : isLoading || isLoadingDA ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {hasSearched ? (
            <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(255, 237, 206, 1)', borderRadius: 2 }}>
              <DATable title="Résultats de recherche" daList={allDAFiltered} onRefresh={handleRefresh} />
            </Paper>
          ) : (
            <>
              <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(255, 237, 206, 1)', borderRadius: 2 }}>
                <DATable title="En attente de paiement" daList={daPending} onRefresh={handleRefresh} />
              </Paper>

              <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(249, 192, 122, 1)', borderRadius: 2 }}>
                <DATable title="En attente de démarches" daList={daProcessing} onRefresh={handleRefresh} />
              </Paper>

              <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(151, 158, 210, 1)', borderRadius: 2 }}>
                <DATable title="Démarches finalisées" daList={daCompleted} onRefresh={handleRefresh} />
              </Paper>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default AgencyDAList;
